import Actor from "./Actor";
import Channel from "./Channel";
import { ConnectionType } from "./Connection";
import BaseModel, { executePaste_Parameters } from "./BaseModel";
import CabinetTreeViewModel from "../modelTreeViews/CabinetTreeviewModel";
import { copyAllProperties } from "./utils";
import BaseObject from "./BaseObject";
import { ChannelType, channelTypeInfo, electricalChannelType, isCablelessPlugChannelType } from "./ChannelType";
import type Consumer from "./Consumer";
import type Page from "./Page";
import BaseModelMap from "./BaseModelMap";
import ConnectableObject from "./ConnectableObject";
import { channelMethode } from "./SettingsEnums";

import Logger from "../Logger";
const logger = new Logger();

//////////////////////////////////////////////////
import ObjectFactory from "./ObjectFactory";
import ELCB from "./ELCB";
import Fuse from "./Fuse";
import TerminalStrip from "./TerminalStrip";
import { itx } from "../translationItx/itx";
import Room from "./Room";

ObjectFactory.registerClass("Cabinet", (data?: any, parent?: BaseModel) => {
  return new Cabinet(data, parent);
});
//////////////////////////////////////////////////

export default class Cabinet extends BaseModel {
  public get className(): string {
    return "Cabinet"; // no itx
  }

  public get isCabinet(): boolean {
    return true;
  }

  // public class properties

  actors = new BaseModelMap<Actor>(Actor, this, "a");
  elcbs = new BaseModelMap<ELCB>(ELCB, this, "e");
  fi_ls_s = new BaseModelMap<Fuse>(Fuse, this, "f");

  terminalStrips = new BaseModelMap<TerminalStrip>(TerminalStrip, this, "ts"); // no itx

  constructor(data?: any, parent?: BaseObject) {
    super(data, parent);
    this.treeViewModel = new CabinetTreeViewModel(this);
    copyAllProperties(data, this);
  }

  actorsColapseMode = new Map<string, number>();
  showUsedActorsOnly = true;

  // save & load

  getPersistentProperties(): string[] {
    return ["actors", "elcbs", "fi_ls_s", "terminalStrips", ...super.getPersistentProperties()]; // no itx
  }

  isMainCabinet(): boolean {
    return this.tag == "" && this.name == "";
  }

  get nameOfDefaultName(): string {
    if (this.tag == "" && this.name == "") {
      return itx("floorCreateAskBack.mainCabinet", "Hauptschaltschrank");
    } else {
      return this.name;
    }
  }

  canBeEditedInteractive(): boolean {
    return false;
  }

  emptyTagIsForbidden(): boolean {
    return state.cabinets.size > 1;
  }

  findConnectableChannel(
    connectionType: ConnectionType,
    partnerObject: ConnectableObject,
    rule?: "hard" | "soft", // no itx
  ): Channel | undefined {
    var res: Channel | undefined;

    logger.log(`findConnectableChannel cabinent`);

    const sortedActors = this.actors.sortBy("tagOrName");
    sortedActors.forEach((actor) => {
      // check if actor is not a special device
      if (
        actor.template?.knxProductData.productInfo.SpecialUsage &&
        actor.template?.knxProductData.productInfo.SpecialUsage != "" &&
        actor.template?.knxProductData.productInfo.SpecialUsage != "Unset"
      ) {
        return;
      }
      const c = actor.findConnectableChannel(connectionType, partnerObject, rule);
      if (c) {
        if (res == null) {
          res = c;
        }
      }
    });

    return res;
  }

  findActorWithSameTypeWithoutTemplateAndNotmaxSize(channelType: ChannelType, isDali?: boolean): Actor | undefined {
    var res: Actor | undefined;
    const sortedActors = this.actors.sortBy("tagOrName");
    isDali = isDali ?? false;
    sortedActors.forEach((actor) => {
      // Supply actors (e.g. 230V-Versorgung) may hold unlimited channels - same exemption as
      // Device.createNewRequestChannel().
      if (
        !actor.template &&
        (actor.channels.size < actor.maxChannelSize || channelTypeInfo(actor.channelType).isSupply)
      ) {
        actor.determineActorType();
        let actorChannelType = actor.channelType;
        if (actorChannelType == ChannelType.Unknown) {
          actorChannelType = actor.getChannelTypeFromChannels();
        }

        if (actorChannelType == channelType) {
          if (actor.isDali === isDali) {
            res = actor;
          }
        } else if (channelTypeInfo(channelType).isRollo && channelTypeInfo(actorChannelType).isRollo) {
          if (actor.isDali === isDali) {
            res = actor;
          }
        }
      }
    });

    return res;
  }

  get isLanCabinet(): boolean {
    return this.actors.asArray().some((a) => a.channelType == ChannelType.Lan);
  }

  getParentChildrenGroup(): Array<BaseModel> | undefined {
    return state.cabinets.sortBy("tag");
  }

  delete(): Array<BaseObject> {
    this.isDeleting = true;

    var res = new Array<BaseObject>();
    res = res.concat(this.actors.deleteAllItems());

    // delete this
    res = res.concat(super.delete());
    return res;
  }

  createActor(data?: any, parent?: BaseModel) {
    const actor = new Actor(data, parent);
    actor.findFreePhysicalAddress();
    actor.checkColapseMode();
    return actor;
  }
  getNotRealisticChannelTypesForActorCreation(): ChannelType[] {
    const res = [
      ChannelType.Disabled,
      ChannelType.Environment,
      ChannelType.General,
      ChannelType.StatusDisplayDimValue,
      ChannelType.StatusDisplaySwitch,
      ChannelType.StatusDisplayTemperature,
      ChannelType.OperationMode,
      ChannelType.UnknownMapWithScript,
      ChannelType.GenericValue,
      ChannelType.Special,
      ChannelType.FromKnxChannel,
      // Lan2 only ever exists on a consumer; a Lan actor's own channelType is always Lan
      ChannelType.Lan2,
    ];
    return res;
  }
  autoCreateActor(channelType: ChannelType, isDali?: boolean): Actor | undefined {
    channelType = electricalChannelType(channelType);
    // LAN cabinet only allows a single LAN actor
    if (this.isLanCabinet && channelType != ChannelType.Lan) {
      return undefined;
    }
    // check first if channel type is realistic
    if (this.getNotRealisticChannelTypesForActorCreation().includes(channelType)) {
      return undefined;
    }
    // count the actor from the same type
    var index = 1;
    this.actors.forEach((actor) => {
      if (actor.channelType == channelType) {
        var index1 = parseInt(actor.tag.replace(/^\D+/g, ""));
        if (Number.isInteger(index1)) {
          index = Math.max(index, index1) + 1;
        }
      }
    });

    let ch = state.translate("components.Actor", "Actor").substring(0, 1);

    var aTag = `${state.translate(`channelType.${channelType}`, channelType).substring(0, 1)}${channelTypeInfo(channelType).isSupply ? "" : ch}${index}`;

    // state.translate(`channelType.${channelType}`, channelType).substring(0, 1) +
    //   `A` +
    //   index.toString();

    const a1 = this.createActor(
      {
        tag: aTag,
        channelMethod: channelMethode.name,
      },
      this,
    );
    if (isDali) {
      a1.isDali = isDali;
    } else {
      a1.channelType = channelType;
    }
    a1.tag = channelTypeInfo(channelType).isSupply ? "" : state.nameAndTagManager.generateTag(a1);
    a1.generateNewNameFromTag();

    // switch (channelType) {
    //   case ChannelType.Unknown:
    //     a1.name = state.translate("components.Actor", "Actor");
    //     break;

    //   case ChannelType.suply230V:
    //     a1.name = "";
    //     a1.tag = "230V";
    //     break;
    //   case ChannelType.suply400V:
    //     a1.tag = "400V";
    //     a1.name = "";
    //     break;
    //   case ChannelType.suply12V:
    //     a1.tag = "12V";  // no itx
    //     a1.name = "";
    //     break;
    //   case ChannelType.suply24V:
    //     a1.tag = "24V";  // no itx
    //     a1.name = "";
    //     break;
    // }

    this.actors.add(a1);
    a1.findFreePhysicalAddress();
    a1.checkColapseMode();

    // mwe 18.2.2024:
    // don't auto create a dummy channel anymore.
    // channels will be autocreated anyway.
    //
    if (false) {
      var channel1: Channel;

      //const channelName = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".substring(i, i + 1);

      const channel = new Channel(
        {
          channelType: channelType,
          name: "A",
          isRequestChannel: true,
        },
        a1,
      );
      a1.channels.add(channel);

      channel1 = channel;
    }

    return a1;
  }

  autoConnectChannel(page: Page, consumer: Consumer, isDali?: boolean): Array<BaseModel> {
    if (isCablelessPlugChannelType(consumer.channelType)) return [];
    var res = new Array<BaseModel>();

    // LAN consumers connect to a LAN actor with free capacity, or a new one is created
    if (channelTypeInfo(consumer.channelType).isLan) {
      let lanActor = this.findActorWithSameTypeWithoutTemplateAndNotmaxSize(ChannelType.Lan);
      if (!lanActor) {
        lanActor = this.autoCreateActor(ChannelType.Lan);
        if (!lanActor) return res;
        res.push(lanActor);
        lanActor.channels.forEach((ch) => res.push(ch));
      }
      // Lan2 consumers (e.g. a double LAN socket) occupy 2 sequential actor-channel slots, shown as a combined tag "1,2"
      lanActor.modifyChannelNumber(lanActor.channels.size + 1, undefined, consumer.channelType);
      const lanChannel = this.findConnectableChannel(ConnectionType.Channel, consumer, "soft");
      if (lanChannel) {
        res.push(lanChannel);
        res.push(lanChannel.connectConsumer(page, consumer));
      }
      return res;
    }

    var channel = this.findConnectableChannel(ConnectionType.Channel, consumer);
    isDali = isDali == undefined ? consumer.isDali : isDali;

    // search first for actorchannel in Room from consumer
    if (channel == undefined) {
      // search first for actorchannel in Room from consumer
      const room = consumer.parent?.parent;
      if (room?.className == "Room") {
        channel = (room as Room).findConnectableActorChannel(ConnectionType.Channel, consumer);
      }
    }

    if (channel == undefined) {
      let actor = this.findActorWithSameTypeWithoutTemplateAndNotmaxSize(electricalChannelType(consumer.channelType), isDali);
      if (actor) {
        actor.modifyChannelNumber(actor.channels.size + 1);
        channel = this.findConnectableChannel(ConnectionType.Channel, consumer, "soft");

        if (channel == undefined) {
          logger.log("Problems with autoconnect new channel in Actor");
        }
      }
    }

    if (channel == undefined) {
      const actor = this.autoCreateActor(electricalChannelType(consumer.channelType), isDali);
      if (!actor) {
        logger.log("Problems with autoconnect new Actor, wrong channel type?");
        return res;
      }
      actor.modifyChannelNumber(1);
      res.push(actor);
      actor.channels.forEach((channel) => {
        res.push(channel);
      });
      channel = actor.channels.sortIntBy("index")[0];
    }

    if (channel) {
      const actor = channel.parent as Actor;
      if (channel.channelType == ChannelType.Unknown && actor.template == undefined) {
        channel.channelType = electricalChannelType(consumer.channelType);
      }
      const c = channel.connectConsumer(page, consumer);
      res.push(c);
    }

    return res;
  }
  //////////////////////////////////////////////////////
  ///////////// for copy
  getExtendedCopyCollection(startItem: BaseModel): Array<BaseModel> {
    const res = new Array<BaseModel>();
    this.actors.forEach((actor) => {
      res.push(actor);
    });

    return res;
  }

  get fullTag(): string {
    const thisParts = this.getFullTagParts();
    var res = "";
    const skipLeadingDots = true;

    thisParts.forEach((part) => {
      res = res + part.prefix + part.tag;
    });

    if (skipLeadingDots && res.startsWith(".")) {
      res = res.substring(1);
    }
    return res;
  }

  getAllTagsFromSameTypeWithoutOwn(): string[] | undefined {
    const res = new Array<string>();

    state.cabinets.forEach((cabinet) => {
      if (this != cabinet) {
        res.push(cabinet.tag);
      }
    });
    return res;
  }
  //////////////////////////////////////////////////////
  ///////////// for paste
  mapAfterCopy() {
    if (!this.mapAfterCopyDone) {
      super.mapAfterCopy();
      this.actors.mapAfterCopy();
    }
  }
  async pasteItem(params: { item: BaseModel; allItems: Array<BaseModel> }): Promise<Array<BaseModel>> {
    const { item } = params;
    if (item.className == "Cabinet") {
      return await item.executePaste({
        parent: state.cabinets,
        isCopyRootObj: true,
      });
    } else if (item.className == "Actor") {
      return await item.executePaste({ parent: this, isCopyRootObj: true });
    }
    return [];
  }

  async executePaste(params: executePaste_Parameters): Promise<Array<BaseModel>> {
    const { parent, isCopyRootObj } = params;
    if (isCopyRootObj) {
      this.updateNameInPaste(state.cabinets);
    }
    if (isCopyRootObj || state.projectMetadata.uniqueTagsForProject) {
      this.updateTagInPaste(state.cabinets);
    }
    this.parent = parent;
    (parent as BaseModelMap<Cabinet>).add(this);
    // paste all Actors
    var undoObjects: Array<BaseModel> = [this];
    for (let actor of this.actors.asArray()) {
      undoObjects.concat(await actor.executePaste({ parent: this, isCopyRootObj: false }));
    }

    return undoObjects;
  }

  getAllFuses(): Array<Fuse> {
    const res = new Array<Fuse>();

    this.elcbs.forEach((elcb) => {
      elcb.fuses.forEach((fuse) => res.push(fuse));
    });
    this.fi_ls_s.forEach((fuse) => res.push(fuse));

    return res;
  }

  updateConnectionEndpoints() {
    setTimeout(() => {
      this.actors.forEach((actor) => actor.updateConnectionEndpoints());
    }, 100);
  }

  hasConnectionOnPage(mainPage: Page, selectedOnly): boolean {
    for (const [key, actor] of this.actors.items) {
      if (actor.hasConnectionOnPage(mainPage, selectedOnly)) {
        return true;
      }
    }

    return false;
  }

  consistencyCheck(parent: BaseModel) {
    super.consistencyCheck(parent);
    this.actors.consistencyCheck(this);
    this.elcbs.consistencyCheck(this);
    this.terminalStrips.consistencyCheck(this);
  }
}
